
















































































































import { Component, Vue, Watch } from 'vue-property-decorator'
import controller from '@/app/ui/controllers/DropBoxController'
import Button from '@/app/ui/components/Button/index.vue'
import TextInput from '@/app/ui/components/TextInput/index.vue'
import DropdownSelect from '@/app/ui/components/DropdownSelect/index.vue'
import DataTable from '@/app/ui/components/DataTable/index.vue'
import PaginationNav from '@/app/ui/components/PaginationNav/index.vue'
import Badge from '@/app/ui/components/Badge/index.vue'
import EmptyState from '@/app/ui/components/EmptyState/EmptyState.vue'
import { Utils } from '@/app/infrastructures/misc'
import AddCircle from '@/app/ui/assets/add_circle.vue'
import { DROP_BOX_PAGINATION } from '@/app/infrastructures/misc/Constants'

interface IParams {
  page: number
  perPage: number
  search: string | null
}

interface IParamsData extends IParams {
  sort: { label: string; value: string }
  filter: { label: string; value: string }
}

interface IParamsValue {
  [key: string]: string | number | boolean | null | undefined
  page: number
  limit: number
  visibility?: string
  name?: string
  sort_by: string
  sort_order: string
}
// Specific interface for DataTable slot scope to avoid any
interface DataTableScope {
  data: string
  index: number
}

@Component({
  components: {
    Button,
    TextInput,
    DropdownSelect,
    DataTable,
    PaginationNav,
    Badge,
    EmptyState,
    AddCircle,
  },
})
export default class DropBoxListPage extends Vue {
  controller = controller
  isGuest = Utils.isGuest()

  filterOptions = [
    { label: 'Show All', value: '' },
    { label: 'Shown', value: 'SHOWN' },
    { label: 'Hidden', value: 'HIDDEN' },
  ]

  sortOptions = [
    { label: 'Newest', value: 'desc' },
    { label: 'Oldest', value: 'asc' },
  ]

  parameters: IParamsData = {
    page: 1,
    perPage: DROP_BOX_PAGINATION,
    search: null,
    sort: this.sortOptions[0],
    filter: this.filterOptions[0],
  }

  headers = [
    { title: 'External ID', width: '350px' },
    { title: 'Name', width: '250px' },
    { title: 'Serial Number', width: '180px' },
    { title: 'PIC Phone Number', width: '200px' },
    { title: 'PIC Agent Code', width: '200px' },
    { title: 'Visibility', width: '120px' },
    { title: 'Action', width: '120px' },
  ]

  created(): void {
    if (this.$route.query instanceof Object) {
      const queries = this.$route.query as Record<string, never>
      // Support both old (perPage) and new (limit) query param names
      const limit =
        Utils.alwaysNumber(queries.limit) || Utils.alwaysNumber(queries.perPage)
      // Support both old (sort) and new (sort_order) query param names
      const sortOrder = queries.sort_order || queries.sort
      // Support both old (filter) and new (visibility) query param names
      const visibility = queries.visibility || queries.filter
      // Support search via name or search
      const search = queries.name || queries.search || null

      this.parameters = {
        page: Utils.alwaysNumber(queries.page) || 1,
        perPage: limit || DROP_BOX_PAGINATION,
        search,
        sort:
          this.sortOptions.find(item => item.value === sortOrder) ||
          this.sortOptions[0],
        filter:
          this.filterOptions.find(item => item.value === visibility) ||
          this.filterOptions[0],
      }
    }
    this.fetchDropBoxList()
  }

  @Watch('params')
  onParamsChanged(
    val: Record<string, string | number | boolean | null | undefined>
  ): void {
    const query: Record<string, string> = {}
    Object.keys(val).forEach(key => {
      const v = val[key]
      if (v !== undefined && v !== null) {
        query[key] = String(v)
      }
    })

    this.$router.replace({ query }).catch(() => {
      // Ignore NavigationDuplicated errors when navigating to the same route
    })
  }

  @Watch('parameters.search')
  onKeywordChanged(val: string): void {
    if (val === '') {
      this.fetchDropBoxList(true)
    }
  }

  get params(): IParamsValue {
    const params: IParamsValue = {
      page: this.parameters.page,
      limit: this.parameters.perPage,
      sort_by: 'updated_at',
      sort_order: this.parameters.sort.value,
    }

    // Map filter to visibility (only if not empty)
    if (this.parameters.filter.value) {
      params.visibility = this.parameters.filter.value
    }

    // Map search to name (supports text and number format)
    if (this.parameters.search) {
      const searchValue = this.parameters.search.trim()
      if (searchValue) {
        params.name = searchValue
      }
    }

    return params
  }

  get dropBoxDataTable(): (string | undefined)[][] {
    return controller.dropBoxData.map(item => [
      item.externalId,
      item.name,
      item.serialNumber,
      item.phoneNumber,
      item.agentCode || '-',
      item.visibility || '-',
      '', // Action column - will be rendered via slot
    ])
  }

  private onCreateDropBox(): void {
    if (!this.isGuest) {
      this.$router.push({ name: 'CreateDropBoxPage' })
    }
  }

  private fetchDropBoxList(resetPage = false): void {
    if (resetPage) {
      this.parameters.page = 1
    }
    this.controller.getDropBoxList(this.params)
  }

  private getVisibilityBadgeType(visibility: string | undefined): string {
    if (!visibility) return 'warning'
    const visibilityUpper = visibility.toUpperCase()
    if (visibilityUpper === 'SHOWN') {
      return 'success'
    }
    if (visibilityUpper === 'HIDDEN') {
      return 'error'
    }
    return 'warning'
  }

  private getActionLink(rowIndex: number): string {
    const externalId =
      controller.dropBoxData[rowIndex]?.externalId ||
      this.dropBoxDataTable[rowIndex][0]
    return `/drop-box/${externalId}`
  }

  // Helper methods for slot-scope (TypeScript workaround)
  private getVisibilityFromScope(scope: DataTableScope): string {
    return scope.data
  }

  private getIndexFromScope(scope: DataTableScope): number {
    return scope.index
  }
}
